import { css, cva } from '@styled-system/css';
import { Lock, Plus, Search, X } from 'lucide-react';
import type { FC, ReactNode } from 'react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { useAnalysisSegment } from '@/features/analysisPage/contexts/AnalysisSegmentContext';
import { Checkbox } from '@/shared/components/ui/Checkbox';
import { Input } from '@/shared/components/ui/Input';
import { Popover } from '@/shared/components/ui/Popover';
import { Tooltip } from '@/shared/components/ui/Tooltip';
import { useGetAnalysisSegmentFacetsQuery, useGetVintedCountriesQuery } from '@/shared/hooks/queries/generated';
import { type SegmentDimension, isDimensionLocked } from '@/shared/utils/analysis/segment';
import { localizeCountryName } from '@/shared/utils/localizeCountry';

// ============================================================================
// Barre de segment globale — attributs d'articles uniquement (état, taille,
// couleur, prix). Elle recalcule TOUS les chiffres de la page (bandeau,
// onglets). Les dimensions fixées par le périmètre de la recherche
// apparaissent en chips verrouillées. La marque n'y figure pas (frontière de
// monétisation : les marques sont les niches payantes).
// ============================================================================

const bar = css({
  display: 'flex',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: '2.5',
});

const chip = cva({
  base: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '2',
    paddingY: '1.5',
    paddingX: '3.5',
    borderRadius: 'full',
    fontSize: 'caption',
    fontFamily: 'sans',
    fontWeight: 'medium',
    borderWidth: '1px',
    borderStyle: 'solid',
    whiteSpace: 'nowrap',
    transition: 'background 120ms ease, color 120ms ease, border-color 120ms ease',
  },
  variants: {
    state: {
      empty: {
        background: 'transparent',
        borderStyle: 'dashed',
        borderColor: 'border.strong',
        color: 'text.tertiary',
        cursor: 'pointer',
        _hover: { color: 'text.primary', borderColor: 'text.tertiary' },
      },
      active: {
        bg: 'brand.primary/10',
        borderColor: 'brand.primary/40',
        color: 'brand.primary',
        cursor: 'pointer',
        _hover: { bg: 'brand.primary/15' },
      },
      locked: {
        bg: 'surface.sunken',
        borderColor: 'border.default',
        color: 'text.tertiary',
        cursor: 'default',
      },
    },
  },
});

const clearIcon = css({
  display: 'inline-flex',
  alignItems: 'center',
  borderRadius: 'full',
  cursor: 'pointer',
  opacity: 0.7,
  _hover: { opacity: 1 },
});

const popoverContent = css({
  display: 'flex',
  flexDirection: 'column',
  gap: '3',
  minWidth: '180px',
});

const optionList = css({
  display: 'flex',
  flexDirection: 'column',
  gap: '2.5',
  maxHeight: '260px',
  overflowY: 'auto',
  paddingRight: '2',
});

const popoverTitle = css({
  fontSize: 'caption',
  color: 'text.tertiary',
  fontWeight: 'medium',
});

// Pastille de couleur devant les options du sélecteur Couleur
const colorSwatch = css({
  width: '12px',
  height: '12px',
  borderRadius: 'full',
  borderWidth: '1px',
  borderStyle: 'solid',
  borderColor: 'border.strong',
  flexShrink: 0,
  display: 'inline-block',
});

const optionEmpty = css({
  fontSize: 'caption',
  color: 'text.tertiary',
  paddingY: '2',
});

const resetButton = css({
  background: 'transparent',
  border: 'none',
  fontSize: 'caption',
  fontFamily: 'sans',
  color: 'text.tertiary',
  cursor: 'pointer',
  padding: '1',
  _hover: { color: 'text.primary', textDecoration: 'underline' },
});

// ─── Résumé des valeurs sélectionnées (2 max + « +N ») ─────────────────────

const summarize = (values: string[]): string => {
  if (values.length <= 2) return values.join(', ');
  return `${values.slice(0, 2).join(', ')} +${values.length - 2}`;
};

// ─── Chip multi-sélection avec popover de checkboxes ───────────────────────

interface MultiSelectChipProps {
  label: string;
  options: { value: string; label: string; swatch?: string }[];
  selected: string[];
  selectedLabels: string[];
  onChange: (selected: string[]) => void;
  /** Barre de recherche en tête du popover (listes longues, ex. tailles). */
  searchable?: boolean;
  searchPlaceholder?: string;
}

const MultiSelectChip: FC<MultiSelectChipProps> = ({
  label,
  options,
  selected,
  selectedLabels,
  onChange,
  searchable = false,
  searchPlaceholder,
}) => {
  const { t } = useTranslation();
  const active = selected.length > 0;
  const [query, setQuery] = useState('');

  const visibleOptions = useMemo(() => {
    if (!searchable || !query.trim()) return options;
    const q = query.trim().toLowerCase();
    return options.filter((option) => option.label.toLowerCase().includes(q));
  }, [options, query, searchable]);

  const toggle = (value: string, checked: boolean) => {
    onChange(checked ? [...selected, value] : selected.filter((v) => v !== value));
  };

  return (
    <Popover
      placement="bottom-start"
      onOpenChange={() => setQuery('')}
      trigger={
        <button type="button" className={chip({ state: active ? 'active' : 'empty' })}>
          {!active && <Plus size={11} strokeWidth={2} />}
          {active ? `${label} · ${summarize(selectedLabels)}` : label}
          {active && (
            <span
              role="button"
              tabIndex={0}
              className={clearIcon}
              onClick={(event) => {
                event.stopPropagation();
                onChange([]);
              }}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.stopPropagation();
                  onChange([]);
                }
              }}
            >
              <X size={12} strokeWidth={2} />
            </span>
          )}
        </button>
      }
    >
      <div className={popoverContent}>
        <span className={popoverTitle}>{label}</span>
        {searchable && (
          <Input
            inputSize="sm"
            leading={<Search size={12} strokeWidth={1.75} />}
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder={searchPlaceholder}
          />
        )}
        <div className={optionList}>
          {visibleOptions.length === 0 ? (
            <span className={optionEmpty}>{t('common.noResults')}</span>
          ) : (
            visibleOptions.map((option) => (
              <Checkbox
                key={option.value}
                checked={selected.includes(option.value)}
                onChange={(checked) => toggle(option.value, checked)}
                label={
                  option.swatch ? (
                    <span className={css({ display: 'inline-flex', alignItems: 'center', gap: '2.5' })}>
                      <span className={colorSwatch} style={{ backgroundColor: option.swatch }} />
                      {option.label}
                    </span>
                  ) : (
                    option.label
                  )
                }
              />
            ))
          )}
        </div>
      </div>
    </Popover>
  );
};

// ─── Chip prix (min / max) ─────────────────────────────────────────────────

interface PriceChipProps {
  label: string;
  min: number | null;
  max: number | null;
  minLabel: string;
  maxLabel: string;
  onChange: (min: number | null, max: number | null) => void;
}

const PriceChip: FC<PriceChipProps> = ({ label, min, max, minLabel, maxLabel, onChange }) => {
  const active = min != null || max != null;
  const summary = min != null && max != null ? `${min}–${max} €` : min != null ? `≥ ${min} €` : `≤ ${max} €`;

  const parse = (raw: string): number | null => {
    if (raw.trim() === '') return null;
    const value = Number(raw);
    return Number.isFinite(value) && value >= 0 ? value : null;
  };

  return (
    <Popover
      placement="bottom-start"
      trigger={
        <button type="button" className={chip({ state: active ? 'active' : 'empty' })}>
          {!active && <Plus size={11} strokeWidth={2} />}
          {active ? `${label} · ${summary}` : label}
          {active && (
            <span
              role="button"
              tabIndex={0}
              className={clearIcon}
              onClick={(event) => {
                event.stopPropagation();
                onChange(null, null);
              }}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.stopPropagation();
                  onChange(null, null);
                }
              }}
            >
              <X size={12} strokeWidth={2} />
            </span>
          )}
        </button>
      }
    >
      <div className={popoverContent}>
        <span className={popoverTitle}>{label}</span>
        <div className={css({ display: 'flex', alignItems: 'center', gap: '3' })}>
          <Input
            type="number"
            inputSize="sm"
            min={0}
            value={min ?? ''}
            onChange={(event) => onChange(parse(event.target.value), max)}
            placeholder={minLabel}
            className={css({ width: '90px' })}
          />
          <span className={css({ color: 'text.tertiary', fontSize: 'caption' })}>—</span>
          <Input
            type="number"
            inputSize="sm"
            min={0}
            value={max ?? ''}
            onChange={(event) => onChange(min, parse(event.target.value))}
            placeholder={maxLabel}
            className={css({ width: '90px' })}
          />
        </div>
      </div>
    </Popover>
  );
};

// ============================================================================

export interface SegmentBarProps {
  /** Dimensions proposées (vendeur : état + prix uniquement). */
  dimensions?: SegmentDimension[];
}

const ALL_DIMENSIONS: SegmentDimension[] = ['condition', 'size', 'color', 'country', 'price'];

export const SegmentBar: FC<SegmentBarProps> = ({ dimensions = ALL_DIMENSIONS }) => {
  const { t } = useTranslation();
  const { id: analysisId = '' } = useParams<{ id: string }>();
  const { segment, isActive, lockedDimensions, platform, updateSegment, resetSegment } = useAnalysisSegment();
  const has = (dimension: SegmentDimension) => dimensions.includes(dimension);

  // Facettes réelles du snapshot (options + comptages) — immuables par analyse
  const { data: facetsData } = useGetAnalysisSegmentFacetsQuery(
    { analysisId },
    { enabled: !!analysisId, staleTime: Infinity, gcTime: 30 * 60 * 1000 },
  );
  const facets = facetsData?.getAnalysisSegmentFacets;

  // 2026-09-04 : facette pays servie par la query principale (back déployé).
  // Une liste vide (analyse d'avant le backfill serveur) masque le menu.
  // Les titres serveur sont FRANÇAIS : on les localise via l'isoCode de
  // getVintedCountries (Intl.DisplayNames), repli sur le titre serveur.
  // VC : hors de propos (table Vinted absente d'emblée, cf countryFacet).
  const { data: countriesData } = useGetVintedCountriesQuery(undefined, {
    enabled: has('country') && platform !== 'vestiaire',
    staleTime: Infinity,
    gcTime: Infinity,
    retry: 1,
  });
  const countryFacet = useMemo(() => {
    const rows = facets?.countries;
    if (!rows?.length) return rows;
    // VC : codes ISO numériques qui CHEVAUCHENT des ids de pays Vinted (4, 10, 12…) — la
    // table Vinted afficherait un autre pays. Le titre serveur est pris tel quel.
    if (platform === 'vestiaire') return rows;
    const isoById = new Map((countriesData?.getVintedCountries ?? []).map((c) => [c.id, c.isoCode]));
    return rows.map((row) => ({ ...row, title: localizeCountryName(isoById.get(row.id), row.title) }));
  }, [facets?.countries, countriesData, platform]);

  const DIMENSION_LABELS: Record<SegmentDimension, string> = {
    condition: t('segment.condition'),
    size: t('segment.size'),
    color: t('segment.color'),
    country: t('segment.country'),
    price: t('segment.price'),
  };

  const toOptions = (
    rows: { id: number; title: string; hex: string | null | undefined; count: number }[] | undefined,
  ) =>
    (rows ?? []).map((row) => ({
      value: String(row.id),
      label: `${row.title} (${row.count})`,
      swatch: row.hex ? (row.hex.startsWith('#') ? row.hex : `#${row.hex}`) : undefined,
    }));

  const titleOf = (rows: { id: number; title: string }[] | undefined, id: number): string =>
    rows?.find((row) => row.id === id)?.title ?? `#${id}`;

  const lockedChips: ReactNode = lockedDimensions
    .filter((locked) => has(locked.dimension))
    .map((locked) => {
      const label = DIMENSION_LABELS[locked.dimension];
      const value = (() => {
        if (locked.dimension === 'price') {
          const [from, to] = locked.values;
          if (from && to) return `${from}–${to} €`;
          return from ? `≥ ${from} €` : `≤ ${to} €`;
        }
        const rows =
          locked.dimension === 'condition'
            ? facets?.conditions
            : locked.dimension === 'size'
              ? facets?.sizes
              : locked.dimension === 'country'
                ? countryFacet
                : facets?.colors;
        return summarize(locked.values.map((id) => titleOf(rows, Number(id))));
      })();

      return (
        <Tooltip key={locked.dimension} label={t('segment.lockedTooltip')}>
          <span className={chip({ state: 'locked' })}>
            <Lock size={11} strokeWidth={2} />
            {`${label} · ${value}`}
          </span>
        </Tooltip>
      );
    });

  const selectedLabels = (rows: { id: number; title: string }[] | undefined, ids: number[]) =>
    ids.map((id) => titleOf(rows, id));

  return (
    <div className={bar}>
      {lockedChips}

      {has('condition') && !isDimensionLocked(lockedDimensions, 'condition') && (
        <MultiSelectChip
          label={DIMENSION_LABELS.condition}
          options={toOptions(facets?.conditions)}
          selected={segment.conditionIds.map(String)}
          selectedLabels={selectedLabels(facets?.conditions, segment.conditionIds)}
          onChange={(ids) => updateSegment({ conditionIds: ids.map(Number) })}
        />
      )}

      {has('size') && !isDimensionLocked(lockedDimensions, 'size') && (
        <MultiSelectChip
          label={DIMENSION_LABELS.size}
          options={toOptions(facets?.sizes)}
          selected={segment.sizeIds.map(String)}
          selectedLabels={selectedLabels(facets?.sizes, segment.sizeIds)}
          onChange={(ids) => updateSegment({ sizeIds: ids.map(Number) })}
          searchable
          searchPlaceholder={t('segment.searchPlaceholder')}
        />
      )}

      {has('color') && !isDimensionLocked(lockedDimensions, 'color') && (
        <MultiSelectChip
          label={DIMENSION_LABELS.color}
          options={toOptions(facets?.colors)}
          selected={segment.colorIds.map(String)}
          selectedLabels={selectedLabels(facets?.colors, segment.colorIds)}
          onChange={(ids) => updateSegment({ colorIds: ids.map(Number) })}
        />
      )}

      {has('country') && !isDimensionLocked(lockedDimensions, 'country') && (countryFacet?.length ?? 0) > 0 && (
        <MultiSelectChip
          label={DIMENSION_LABELS.country}
          options={toOptions(countryFacet)}
          selected={segment.countryIds.map(String)}
          selectedLabels={selectedLabels(countryFacet, segment.countryIds)}
          onChange={(ids) => updateSegment({ countryIds: ids.map(Number) })}
          searchable
          searchPlaceholder={t('segment.searchPlaceholder')}
        />
      )}

      {has('price') && !isDimensionLocked(lockedDimensions, 'price') && (
        <PriceChip
          label={DIMENSION_LABELS.price}
          min={segment.priceMin}
          max={segment.priceMax}
          minLabel={facets?.priceMin != null ? String(Math.floor(facets.priceMin)) : t('segment.min')}
          maxLabel={facets?.priceMax != null ? String(Math.ceil(facets.priceMax)) : t('segment.max')}
          onChange={(priceMin, priceMax) => updateSegment({ priceMin, priceMax })}
        />
      )}

      {isActive && (
        <button type="button" className={resetButton} onClick={resetSegment}>
          {t('segment.reset')}
        </button>
      )}
    </div>
  );
};
